import type { SaveStep } from "@lancer-vtt/domain";

/**
 * The verb a result card prints between a rolled total and the number it was checked against:
 * `18 beats EVA 10`, `10 meets EVA 10`, `8 short of EVA 10` (`combat-surface-polish` R1, D2).
 *
 * RAW, an attack hits when the roll equals or exceeds the defense, and a save or check passes on the
 * same test against its DC (Core, "Attacks" and "Skill checks"). A crit still reads `beats` or
 * `meets`; the CRIT stamp carries the crit.
 *
 * **The verb follows the recorded result, never re-derives it.** When the recorded result and the
 * arithmetic disagree (an effect forced a hit or a miss), this returns `vs`, so the verb never
 * contradicts the stamp printed beside it.
 */
export type RollVerdict = "beats" | "meets" | "short of" | "vs";

/** `total` checked against `target` (a defense or a DC), with the result the record stored. */
export function rollVerdict(total: number, target: number, result: "hit" | "crit" | "miss" | "pass" | "fail"): RollVerdict {
  const succeeded = result !== "miss" && result !== "fail";
  if (succeeded) return total > target ? "beats" : total === target ? "meets" : "vs";
  return total < target ? "short of" : "vs";
}

/**
 * A save or check against its DC as a warning line prints it: `14 beats DC 10`, `8 short of DC 10`,
 * or `no die drawn · DC 10` for a conceded or auto-failed save, which compared no total.
 */
export function saveVerdictText(save: Pick<SaveStep, "total" | "dc" | "result" | "conceded" | "autoFailed">): string {
  if (save.conceded === true || save.autoFailed === true) return `no die drawn · DC ${save.dc}`;
  return `${save.total} ${rollVerdict(save.total, save.dc, save.result)} DC ${save.dc}`;
}
